import { Base } from '../../abstract/base.model';
import { isRecord } from '../utils/is-record';
import { nullableBoolean, nullableString, nullableStringArray } from '../utils/wire-value-utils';
import { ChemicalLabel } from './chemical-label.model';

/**
 * An ADN Recommended chemical, built from `/admin/chemicals` JSON (hub `data-models/chemical.ts`,
 * `admin-chemicals-controller.ts`). The detail route adds `labels`.
 *
 * Read-modify-write rule: writable fields the hub didn't send stay `undefined` (an explicit `null` stays
 * `null`). Display fallbacks belong in templates. A chemical is never a request body: use `toChemicalRequest`.
 */
export class Chemical extends Base {
  // Writable via PUT /admin/chemicals/:id (the hub's CHEMICAL_WRITABLE)
  name?: string | null;
  category?: string | null;
  signalWord?: string | null;
  epaNumber?: string | null;
  manufacturer?: string | null;
  activeIngredient?: string | null;
  formulation?: string | null;
  defaultRate?: string | null;
  rei?: string | null;
  phi?: string | null;
  targetPests?: string[] | null;
  crops?: string[] | null;
  sponsorId?: string | null;
  sponsorName?: string | null;
  sponsorLogoUrl?: string | null;
  sponsorWebsite?: string | null;
  sponsorPhone?: string | null;
  sponsorEmail?: string | null;
  isFeatured?: boolean | null;
  specialOffer?: string | null;
  compareTo?: string | null;
  rupFlag?: boolean | null;
  sdsUrl?: string | null;
  productUrl?: string | null;

  // Read-only, set by the hub
  source?: string | null;
  /** ISO timestamp as sent by the hub. */
  archivedAt?: string | null;
  archivedBy?: string | null;
  /** Only on GET /admin/chemicals/:id; `undefined` on list rows. */
  labels?: ChemicalLabel[];

  constructor(data: unknown) {
    super(data);
    const source = isRecord(data) ? data : {};
    this.readIdentity(source);
    this.readSponsor(source);
    this.source = nullableString(source['source']);
    this.archivedAt = nullableString(source['archivedAt']);
    this.archivedBy = nullableString(source['archivedBy']);
    const labels = source['labels'];
    this.labels = Array.isArray(labels) ? labels.map(item => new ChemicalLabel(item)) : undefined;
  }

  private readIdentity(source: Record<string, unknown>): void {
    this.name = nullableString(source['name']);
    this.category = nullableString(source['category']);
    this.signalWord = nullableString(source['signalWord']);
    this.epaNumber = nullableString(source['epaNumber']);
    this.manufacturer = nullableString(source['manufacturer']);
    this.activeIngredient = nullableString(source['activeIngredient']);
    this.formulation = nullableString(source['formulation']);
    this.defaultRate = nullableString(source['defaultRate']);
    this.rei = nullableString(source['rei']);
    this.phi = nullableString(source['phi']);
    this.targetPests = nullableStringArray(source['targetPests']);
    this.crops = nullableStringArray(source['crops']);
    this.compareTo = nullableString(source['compareTo']);
    this.rupFlag = nullableBoolean(source['rupFlag']);
    this.sdsUrl = nullableString(source['sdsUrl']);
    this.productUrl = nullableString(source['productUrl']);
  }

  private readSponsor(source: Record<string, unknown>): void {
    this.sponsorId = nullableString(source['sponsorId']);
    this.sponsorName = nullableString(source['sponsorName']);
    this.sponsorLogoUrl = nullableString(source['sponsorLogoUrl']);
    this.sponsorWebsite = nullableString(source['sponsorWebsite']);
    this.sponsorPhone = nullableString(source['sponsorPhone']);
    this.sponsorEmail = nullableString(source['sponsorEmail']);
    this.isFeatured = nullableBoolean(source['isFeatured']);
    this.specialOffer = nullableString(source['specialOffer']);
  }
}
